/**
 * Helper functions for manipulating the Draft.js EditorState data structure
 *
 * 
 */

import React from 'react'
import { RichUtils, Modifier, EditorState, SelectionState } from 'draft-js'
import getRangesForDraftEntity from 'draft-js/lib/getRangesForDraftEntity'
import { Intent } from '@blueprintjs/core'


// We need the selection to remain visible while the user interacts with the
// edgenote creation popover, so we add an inline style of type "SELECTION",
// which gives a grey background.
export function addShadowSelection (editorState) {
  if (!editorState.getSelection().isCollapsed()) {
    return RichUtils.toggleInlineStyle(editorState, 'SELECTION')
  } else {
    return editorState
  }
}

export function removeShadowSelection (editorState) {
  if (editorState.getCurrentInlineStyle().has('SELECTION')) {
    return RichUtils.toggleInlineStyle(editorState, 'SELECTION')
  } else {
    return editorState
  }
}

export function addEntity (
  {
    type,
    mutability,
    data,
  },
  editorState,
  selection = editorState.getSelection(),
  contentState = editorState.getCurrentContent()
) {
  const contentStateWithEntity = contentState.createEntity(
    type,
    mutability,
    data
  )
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
  const contentStateWithEntityApplied = Modifier.applyEntity(
    contentStateWithEntity,
    selection,
    entityKey
  )

  const editorStateWithEntity = EditorState.push(
    editorState,
    contentStateWithEntityApplied,
    'apply-entity'
  )
  return editorStateWithEntity
}

export function addEdgenoteEntity (
  slug,
  editorState
) {
  const edgenoteEntityProps = {
    type: 'EDGENOTE',
    mutability: 'MUTABLE',
    data: { slug },
  }
  return addEntity(edgenoteEntityProps, editorState)
}

const getEntitySelectionState = (
  contentState,
  selectionState,
  entityKey
) => {
  const selectionKey = selectionState.getAnchorKey()
  const selectionOffset = selectionState.getAnchorOffset()
  const block = contentState.getBlockForKey(selectionKey)
  const blockKey = block.getKey()

  let entitySelection = null
  getRangesForDraftEntity(block, entityKey).forEach(range => {
    if (range.start <= selectionOffset && selectionOffset <= range.end) {
      entitySelection = new SelectionState({
        anchorOffset: range.start,
        anchorKey: blockKey,
        focusOffset: range.end,
        focusKey: blockKey,
        isBackward: false,
        hasFocus: selectionState.getHasFocus(),
      })
    }
  })
  return entitySelection
}

export function removeSelectedEntity (editorState) {
  const entityKey = getSelectedEntityKey(editorState)
  if (entityKey == null) return editorState

  const entitySelection = getEntitySelectionState(
    editorState.getCurrentContent(),
    editorState.getSelection(),
    entityKey
  )
  if (entitySelection == null) return editorState

  const withoutEntity = Modifier.applyEntity(
    editorState.getCurrentContent(),
    entitySelection,
    null
  )
  return EditorState.push(editorState, withoutEntity, 'apply-entity')
}

export function getSelectedEntityKey (editorState) {
  const selection = editorState.getSelection()
  return editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getEntityAt(selection.getStartOffset())
}

// Is the block under the cursor of the given type?
export const blockTypeEquals = (type) => (editorState) =>
  editorState
    .getCurrentContent()
    .getBlockForKey(editorState.getSelection().getStartKey())
    .getType() === type

// Is the entity under the cursor of the given type?
export const entityTypeEquals = (type) => (
  editorState
) => {
  const entityKey = getSelectedEntityKey(editorState)
  if (entityKey == null) return false

  return (
    editorState
      .getCurrentContent()
      .getEntity(entityKey)
      .getType() === type
  )
}

export async function toggleEdgenote (
  editorState,
  { displayToast, getEdgenote, intl }
) {
  if (getEdgenote == null) return editorState

  if (entityTypeEquals('EDGENOTE')(editorState)) {
    return removeSelectedEntity(editorState)
  }

  if (editorState.getSelection().isCollapsed()) {
    displayToast({
      icon: 'error',
      intent: Intent.WARNING,
      message: (
        <span
          className="bp6-dark"
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({
              id: 'edgenotes.new.makeSelectionHtml',
            }),
          }}
        />
      ),
    })
    return editorState
  }

  return getEdgenote().then(slug => addEdgenoteEntity(slug, editorState))
}

export function addCitationEntity (
  editorState,
  { displayToast, intl }
) {
  let selection = editorState.getSelection()

  const collapsedSelection = selection.merge({
    anchorOffset: selection.getEndOffset(),
    focusOffset: selection.getEndOffset(),
  })

  const contentStateWithCircle = Modifier.insertText(
    editorState.getCurrentContent(),
    collapsedSelection,
    '°'
  )

  const circleSelection = collapsedSelection.merge({
    anchorOffset: collapsedSelection.focusOffset,
    focusOffset: collapsedSelection.focusOffset + 1,
  })

  displayToast({
    icon: 'tick',
    intent: Intent.SUCCESS,
    message: (
      <span
        className="bp6-dark"
        dangerouslySetInnerHTML={{
          __html: intl.formatMessage({
            id: 'cards.edit.citationAdded',
          }),
        }}
      />
    ),
  })

  return addEntity(
    { type: 'CITATION', mutability: 'IMMUTABLE', data: {}},
    editorState,
    circleSelection,
    contentStateWithCircle
  )
}

export async function toggleMath (
  editorState,
  { cardId, displayToast, intl }
) {
  if (entityTypeEquals('MATH')(editorState)) {
    return removeSelectedEntity(editorState)
  }

  if (editorState.getSelection().isCollapsed()) {
    displayToast({
      icon: 'error',
      intent: Intent.WARNING,
      message: (
        <span
          className="bp6-dark"
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({
              id: 'cards.edit.mathInstructions',
            }),
          }}
        />
      ),
    })
    return editorState
  }

  displayToast({
    icon: 'tick',
    intent: Intent.SUCCESS,
    message: (
      <span
        className="bp6-dark"
        dangerouslySetInnerHTML={{
          __html: intl.formatMessage({
            id: 'cards.edit.mathAdded',
          }),
        }}
      />
    ),
  })

  return addEntity({
    type: 'MATH',
    mutability: 'IMMUTABLE',
    data: { cardId },
  }, editorState)
}

export async function toggleRevealableEntity (
  editorState,
  { cardId, displayToast, intl }
) {
  if (entityTypeEquals('REVEALABLE')(editorState)) {
    return removeSelectedEntity(editorState)
  }

  if (editorState.getSelection().isCollapsed()) {
    displayToast({
      icon: 'error',
      intent: Intent.WARNING,
      message: (
        <span
          className="bp6-dark"
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({
              id: 'cards.edit.makeRevealableSelection',
            }),
          }}
        />
      ),
    })
    return editorState
  }

  return addEntity({
    type: 'REVEALABLE',
    mutability: 'MUTABLE',
    data: { cardId },
  }, editorState)
}

export async function toggleSubscript (editorState) {
  if (entityTypeEquals('SUBSCRIPT')(editorState)) {
    return removeSelectedEntity(editorState)
  }

  if (editorState.getSelection().isCollapsed()) {
    return editorState
  }

  return addEntity({
    type: 'SUBSCRIPT',
    mutability: 'MUTABLE',
    data: {},
  }, editorState)
}

export async function toggleSuperscript (editorState) {
  if (entityTypeEquals('SUPERSCRIPT')(editorState)) {
    return removeSelectedEntity(editorState)
  }

  if (editorState.getSelection().isCollapsed()) {
    return editorState
  }

  return addEntity({
    type: 'SUPERSCRIPT',
    mutability: 'MUTABLE',
    data: {},
  }, editorState)
}

export function clearEditorContent (editorState) {
  const contentState = editorState.getCurrentContent()
  const firstBlock = contentState.getFirstBlock()
  const lastBlock = contentState.getLastBlock()

  // Create selection that spans all content
  const fullSelection = SelectionState.createEmpty(firstBlock.getKey()).merge({
    anchorOffset: 0,
    focusKey: lastBlock.getKey(),
    focusOffset: lastBlock.getLength(),
    hasFocus: true,
    isBackward: false,
  })

  // Remove all content
  const clearedContentState = Modifier.removeRange(
    contentState,
    fullSelection,
    'backward'
  )

  // Create new editor state and move cursor to beginning
  const clearedEditorState = EditorState.push(
    editorState,
    clearedContentState,
    'remove-range'
  )

  // Ensure cursor is at the beginning
  return EditorState.forceSelection(
    clearedEditorState,
    SelectionState.createEmpty(clearedContentState.getFirstBlock().getKey())
  )
}
